/* eslint-disable no-inner-declarations */
import { useCallback, useEffect, useRef } from "react";
import { AudioPlayerProps } from "~/interface/audioplayer/audioplayer";
import { addToCurrentAudio, changeIsPlayingState, setCurrentAudioTime, setDuration, setProgress } from "~/lib/store/slice/audioSlice";
import { setCurrentVideoPosition, setYoutubeVideoIsPlay } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { AUDIO_PLAYER, VIDEO_PLAYER } from "~/utils/constant";

const AudioPlayerCore = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dispatch = useAppDispatch();
  const { currentAudio, isPlaying, audioQueue, currentTime, playbackSpeed, progress, duration } = useAppSelector((state) => state.nityNiyam);
  const isPlayingRef = useRef(isPlaying);

  const playNext = useCallback(() => {
    const audioQueueInSession = JSON.parse(sessionStorage.getItem(AUDIO_PLAYER.AUDIO_QUEUE) || "[]");
    if (audioQueueInSession && audioQueueInSession.length > 0) {
      let nextAudio;

      const currentAudioIndex = audioQueueInSession.findIndex((item: AudioPlayerProps) => {
        return item.id === currentAudio?.id;
      });
      if (currentAudioIndex !== undefined && currentAudioIndex !== -1) {
        if (currentAudioIndex + 1 >= audioQueueInSession.length) {
          // Playlist ended - stop playback
          dispatch(changeIsPlayingState(false));
          return;
        } else {
          nextAudio = audioQueueInSession[currentAudioIndex + 1];
        }
      }

      if (nextAudio) {
        // Clear session storage and load next audio
        sessionStorage.removeItem(AUDIO_PLAYER.CURRENT_TIME);
        dispatch(setProgress(0));
        dispatch(setCurrentAudioTime(0));
        dispatch(addToCurrentAudio(nextAudio));
        dispatch(changeIsPlayingState(true));
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      }
    } else {
      // No queue - stop playback
      dispatch(changeIsPlayingState(false));
      sessionStorage.removeItem(AUDIO_PLAYER.CURRENT_TIME);
    }
  }, [currentAudio?.id, dispatch]);

  const playPrevious = useCallback(() => {
    const currentAudioIndex = audioQueue?.findIndex((item) => item.id === currentAudio?.id);

    if (currentAudioIndex !== undefined && currentAudioIndex !== -1) {
      const previousAudio = audioQueue?.[(currentAudioIndex - 1 + audioQueue.length) % audioQueue.length];
      dispatch(setProgress(0));
      dispatch(setCurrentAudioTime(0));
      dispatch(changeIsPlayingState(true));
      if (previousAudio) {
        dispatch(addToCurrentAudio(previousAudio));
      }
      sessionStorage.setItem(AUDIO_PLAYER.CURRENT_AUDIO, JSON.stringify(previousAudio));
    }
  }, [currentAudio?.id, audioQueue, dispatch]);

  const togglePlayPause = useCallback(() => {
    const newPlayingState = !isPlayingRef.current;

    if (newPlayingState) {
      sessionStorage.removeItem(VIDEO_PLAYER.CURRENT_VIDEO);
      sessionStorage.removeItem(VIDEO_PLAYER.IS_VIDEO_PLAY);
      sessionStorage.removeItem(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
      dispatch(setCurrentVideoPosition(0));
      dispatch(setYoutubeVideoIsPlay(false));
    }

    dispatch(changeIsPlayingState(newPlayingState));
  }, [dispatch]);

  // Keep ref in sync with state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && currentAudio?.audio) {
      if (isPlaying) {
        audio.play().catch((error) => console.error("Playback failed:", error));
      } else {
        audio.pause();
      }
    }
  }, [isPlaying, currentAudio?.audio]);

  // Initialize Media Session metadata only when audio changes
  useEffect(() => {
    if ("mediaSession" in navigator && currentAudio?.audio) {
      // Create multiple artwork sizes for better compatibility
      const artwork = [];

      // Primary image from currentAudio
      if (currentAudio?.img) {
        artwork.push({
          src: currentAudio.img,
          type: "image/png",
          sizes: "512x512",
        });
        artwork.push({
          src: currentAudio.img,
          type: "image/png",
          sizes: "256x256",
        });
        artwork.push({
          src: currentAudio.img,
          type: "image/png",
          sizes: "128x128",
        });
        artwork.push({
          src: currentAudio.img,
          type: "image/png",
          sizes: "96x96",
        });
      } else {
        // Fallback images with multiple sizes
        artwork.push(
          {
            src: "./images/audioPlayerThumbnail.png",
            type: "image/png",
            sizes: "512x512",
          },
          {
            src: "./images/audioPlayerThumbnail.png",
            type: "image/png",
            sizes: "256x256",
          },
          {
            src: "./images/audioPlayerThumbnail.png",
            type: "image/png",
            sizes: "128x128",
          },
          {
            src: "./images/audioPlayerThumbnail.png",
            type: "image/png",
            sizes: "96x96",
          },
        );
      }

      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentAudio?.name,
        artist: currentAudio?.artist ?? currentAudio?.artist !== null ? currentAudio?.artist : "www.swaminarayanbhagawan.org",
        album: currentAudio?.img || "Swaminarayan Bhagawan",
        artwork: artwork,
      });
    }
  }, [currentAudio?.audio, currentAudio?.name, currentAudio?.artist, currentAudio?.img]);

  // Update action handlers when state changes (without destroying notification)
  useEffect(() => {
    if ("mediaSession" in navigator && audioRef.current && currentAudio?.audio) {
      const audio = audioRef.current;

      navigator.mediaSession.setActionHandler("play", togglePlayPause);
      navigator.mediaSession.setActionHandler("pause", togglePlayPause);
      navigator.mediaSession.setActionHandler("previoustrack", playPrevious);
      navigator.mediaSession.setActionHandler("nexttrack", playNext);

      // iOS specific: Add seek handlers for forward/rewind on lock screen
      try {
        navigator.mediaSession.setActionHandler("seekforward", (details) => {
          const skipTime = details.seekOffset || 15;
          if (audio) {
            audio.currentTime = Math.min(audio.currentTime + skipTime, audio.duration);
          }
        });

        navigator.mediaSession.setActionHandler("seekbackward", (details) => {
          const skipTime = details.seekOffset || 15;
          if (audio) {
            audio.currentTime = Math.max(audio.currentTime - skipTime, 0);
          }
        });

        navigator.mediaSession.setActionHandler("seekto", (details) => {
          if (audio && details.seekTime) {
            audio.currentTime = details.seekTime;
          }
        });
      } catch (error) {
        // Silently fail on unsupported browsers
      }
    }
  }, [togglePlayPause, playNext, playPrevious, currentAudio?.audio]);

  // Update Media Session position state continuously (without recreating the session)
  useEffect(() => {
    if ("mediaSession" in navigator && audioRef.current) {
      const audio = audioRef.current;

      function updateMediaSession() {
        if (audio && !isNaN(audio.duration)) {
          const position = Math.min(audio.currentTime, audio.duration);
          navigator.mediaSession.setPositionState({
            duration: audio?.duration || 0,
            position: position,
            playbackRate: audio.playbackRate || 1,
          });
        }
      }

      // Update progress every second
      const interval = setInterval(updateMediaSession, 1000);

      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      if (audio) {
        dispatch(setDuration(Number(audio.duration)));
        // Ensure playback rate is maintained after metadata loads
        audio.playbackRate = playbackSpeed;
      }
    };

    const handleTimeUpdate = () => {
      if (audio) {
        dispatch(setCurrentAudioTime(audio.currentTime));
        sessionStorage.setItem(AUDIO_PLAYER.CURRENT_TIME, audio.currentTime.toString());
      }
    };

    const handleEnded = () => {
      playNext();
    };

    if (audio && currentAudio?.audio) {
      // Only reset and set src when currentAudio changes
      dispatch(setCurrentAudioTime(0));
      dispatch(setDuration(audio.duration));
      sessionStorage.removeItem(AUDIO_PLAYER.CURRENT_TIME);

      audio.src = currentAudio.audio;
      
      // Immediately set playback rate after setting new source
      audio.playbackRate = playbackSpeed;

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);

      // Play/pause is now handled by the separate isPlaying useEffect
      if (isPlaying) {
        audio.play().catch((error) => console.error("Playback failed:", error));
      } else {
        audio.pause();
      }
    }

    return () => {
      if (audio) {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
      }
    };
  }, [currentAudio?.audio, dispatch]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    // Update progress whenever audio time updates
    const updateProgress = () => {
      if (audio.duration > 0) {
        const currentProgress = (audio.currentTime / audio.duration) * 100;
        dispatch(setProgress(currentProgress));
      }
    };

    audio.addEventListener("timeupdate", updateProgress);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
    };
  }, []);

  return <audio ref={audioRef} className="Thalplayer" />;
};

export default AudioPlayerCore;
